import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Users, Calendar, CreditCard, ArrowLeft, RefreshCw, Sparkles } from "lucide-react";
import { API, POLLING_INTERVAL_MS, StatCard, DataTable, formatDate, StatusBadge } from "./components/admin/shared";
import AnalyticsChart from "./components/admin/AnalyticsChart";
import AppointmentsTab from "./components/admin/AppointmentsTab";
import BlogTab from "./components/admin/BlogTab";
import AIContentTab from "./components/admin/AIContentTab";

export default function AdminPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({ contacts: 0, bookings: 0, total_orders: 0, paid_orders: 0 });
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({ enrollments_by_date: [], contacts_by_date: [] });
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async (authToken) => {
    try {
      const t = authToken || token;
      const headers = { Authorization: `Bearer ${t}` };
      const [s, c, b, e, a, appts, ld] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/enrollments`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/appointments`, { headers }),
        axios.get(`${API}/admin/leads`, { headers })
      ]);
      setStats(s.data);
      setContacts(c.data.contacts);
      setBookings(b.data.bookings);
      setEnrollments(e.data.enrollments);
      setAnalytics(a.data);
      setAppointments(appts.data.appointments);
      setLeads(ld.data.leads);
      setFetchError(null);
    } catch (err) {
      setFetchError(err.response?.status === 401 ? "Session expired. Please re-login." : "Failed to refresh data.");
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const authToken = res.data.token;
      setToken(authToken);
      setAuthed(true);
      fetchData(authToken);
    } catch {
      setError("Invalid password");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (authed) {
      const interval = setInterval(() => fetchData(), POLLING_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [authed, fetchData]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-4">
        <div data-testid="admin-login-form" className="bg-white rounded-2xl p-8 shadow-lg w-full max-w-sm border border-emerald_deep/5">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-emerald_deep flex items-center justify-center mx-auto mb-4">
              <Lock size={22} className="text-bone" />
            </div>
            <h1 className="font-serif text-2xl text-emerald_deep">Admin Dashboard</h1>
            <p className="text-sm text-text_muted mt-1">Fitly India</p>
          </div>
          <form onSubmit={handleLogin}>
            <input data-testid="admin-password-input" type="password" placeholder="Enter admin password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-emerald_deep/10 bg-bone/50 text-emerald_deep placeholder:text-text_muted/50 focus:outline-none focus:ring-2 focus:ring-accent/40 mb-4" />
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <button data-testid="admin-login-btn" type="submit" disabled={loading || !password}
              className="w-full bg-emerald_deep text-bone py-3 rounded-xl font-medium hover:bg-emerald_deep/90 transition-all disabled:opacity-50">
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const pendingAppts = appointments.filter(a => a.appointment_status === "pending_confirmation" && a.status === "completed").length;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "appointments", label: `Appointments${pendingAppts ? ` (${pendingAppts})` : ""}` },
    { id: "blog", label: "Blog" },
    { id: "ai-content", label: "AI Content" },
    { id: "contacts", label: `Contacts (${stats.contacts})` },
    { id: "bookings", label: `Bookings (${stats.bookings})` },
    { id: "payments", label: `Payments (${stats.total_orders})` },
    { id: "leads", label: `Leads (${leads.length})` }
  ];

  const contactCols = [
    { key: "name", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "health_issues", label: "Health Concern" },
    { key: "created_at", label: "Date", render: formatDate }
  ];

  const bookingCols = [
    { key: "name", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "preferred_date", label: "Pref. Date" },
    { key: "preferred_time", label: "Pref. Time" },
    { key: "message", label: "Message" },
    { key: "created_at", label: "Submitted", render: formatDate }
  ];

  const enrollmentCols = [
    { key: "customer_name", label: "Customer" },
    { key: "customer_phone", label: "Phone" },
    { key: "program_name", label: "Program" },
    { key: "amount", label: "Amount", render: (v) => `₹${(v / 100).toLocaleString()}` },
    { key: "status", label: "Status", render: StatusBadge },
    { key: "razorpay_order_id", label: "Order ID" },
    { key: "created_at", label: "Date", render: formatDate }
  ];

  return (
    <div className="min-h-screen bg-bone">
      <header className="bg-white border-b border-emerald_deep/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button data-testid="admin-back-btn" onClick={() => navigate("/")} className="text-text_muted hover:text-emerald_deep transition-colors">
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-serif text-xl text-emerald_deep">Fitly Admin</h1>
          </div>
          <button data-testid="admin-refresh-btn" onClick={() => fetchData()}
            className="flex items-center gap-2 text-sm text-text_muted hover:text-emerald_deep transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {fetchError && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-lg mb-4" data-testid="fetch-error-banner">
            {fetchError}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Contacts" value={stats.contacts} color="bg-emerald_deep" />
          <StatCard icon={Calendar} label="Bookings" value={stats.bookings} color="bg-accent" />
          <StatCard icon={CreditCard} label="Total Orders" value={stats.total_orders} color="bg-emerald_deep/80" />
          <StatCard icon={CreditCard} label="Paid Orders" value={stats.paid_orders} color="bg-green-600" />
        </div>

        <AnalyticsChart analytics={analytics} />

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                tab === t.id ? "bg-emerald_deep text-bone" : "bg-white text-text_muted hover:text-emerald_deep border border-emerald_deep/5"
              }`}>{t.label}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-6">
            <DataTable title="Recent Contacts" data={contacts.slice(0, 5)} columns={contactCols} emptyMsg="No contact submissions yet" testId="recent-contacts-table" />
            <DataTable title="Recent Payments" data={enrollments.slice(0, 5)} columns={enrollmentCols} emptyMsg="No payments yet" testId="recent-payments-table" />
          </div>
        )}
        {tab === "appointments" && <AppointmentsTab appointments={appointments} token={token} onRefresh={() => fetchData()} />}
        {tab === "blog" && <BlogTab token={token} onRefresh={() => fetchData()} />}
        {tab === "ai-content" && <AIContentTab token={token} />}
        {tab === "contacts" && <DataTable title="All Contacts" data={contacts} columns={contactCols} emptyMsg="No contact submissions yet" testId="all-contacts-table" />}
        {tab === "bookings" && <DataTable title="All Bookings" data={bookings} columns={bookingCols} emptyMsg="No bookings yet" testId="all-bookings-table" />}
        {tab === "payments" && <DataTable title="All Payments" data={enrollments} columns={enrollmentCols} emptyMsg="No payments yet" testId="all-payments-table" />}
        {tab === "leads" && <DataTable title="Lead Captures" data={leads} columns={[
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone" },
          { key: "source_page", label: "Source Page" },
          { key: "created_at", label: "Captured", render: formatDate }
        ]} emptyMsg="No leads captured yet" testId="leads-table" />}
      </div>
    </div>
  );
}
